import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { List, Columns, AlertTriangle, Calendar, Building2, User as UserIcon, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui/Spinner';
import { useRequests } from '@/api/requests';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { useIsClient } from '@/hooks/useIsClient';
import { usePageTitle } from '@/hooks/usePageTitle';
import { cn } from '@/lib/cn';
import type { FileRequest, RequestStatus } from '@/types/api';
import { apiError } from '@/lib/api';

interface KanbanColumn {
  label: string;
  statuses: RequestStatus[];
  color: string;
}

const COLUMNS: KanbanColumn[] = [
  { label: 'Open', statuses: ['open', 'overdue'], color: 'border-t-blue-500' },
  { label: 'Submitted', statuses: ['submitted'], color: 'border-t-yellow-500' },
  { label: 'Under Review', statuses: ['under_review'], color: 'border-t-purple-500' },
  { label: 'Accepted', statuses: ['accepted'], color: 'border-t-green-500' },
  { label: 'Rejected / Cancelled', statuses: ['rejected', 'cancelled'], color: 'border-t-red-500' },
];

function RequestCard({ req, isClient }: { req: FileRequest; isClient: boolean }) {
  const isOverdue = req.is_overdue || req.status === 'overdue';
  const isRejected = req.status === 'rejected';
  return (
    <Link
      to={`/requests/${req.id}`}
      className={cn(
        'block bg-white dark:bg-[#1A1A2E] border p-3 hover:border-bb-navy/40 dark:hover:border-bb-navy/60 transition-colors',
        isRejected
          ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
          : 'border-slate-200 dark:border-[#2A2A4A]',
      )}
    >
      <div className="text-sm font-medium text-slate-800 dark:text-white line-clamp-2">{req.title}</div>
      {isRejected && (
        <div className="flex items-center gap-1 mt-1.5 text-xs text-red-600 font-medium">
          <XCircle size={11} /> Rejected — tap to resubmit
        </div>
      )}
      <div className="mt-2 space-y-1">
        {req.due_date && (
          <div className={cn('flex items-center gap-1 text-xs', isOverdue ? 'text-red-600' : 'text-slate-500 dark:text-[#8888AA]')}>
            {isOverdue ? <AlertTriangle size={11} /> : <Calendar size={11} />}
            {isOverdue ? 'Overdue · ' : 'Due '}
            {format(new Date(req.due_date), 'MMM d, yyyy')}
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-[#8888AA]">
          <Calendar size={11} className="shrink-0" />
          <span>Created {format(new Date(req.created_at), 'MMM d, yyyy')}</span>
        </div>
        {(isClient ? true : req.requestor_name) && (
          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-[#8888AA]">
            <UserIcon size={11} className="shrink-0" />
            <span className="truncate">{isClient ? 'BillerBay' : req.requestor_name}</span>
          </div>
        )}
        {req.organization_name && !isClient && (
          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-[#8888AA]">
            <Building2 size={11} className="shrink-0" />
            <span className="truncate">{req.organization_name}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

export function RequestsKanbanPage() {
  usePageTitle('File Requests — Board');
  const { user } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const isClient = useIsClient();

  const { data, isLoading, isError, error } = useRequests({
    org_id: activeOrgId ?? user?.organization_id ?? null,
    limit: 200,
    offset: 0,
  });

  const grouped = useMemo(() => {
    const items = data?.items ?? [];
    return COLUMNS.map((col) => ({
      ...col,
      requests: items.filter((r) => (col.statuses as string[]).includes(r.status)),
    }));
  }, [data]);

  return (
    <AppShell crumbs={[{ label: 'Requests', to: '/requests' }, { label: 'Board' }]}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-bb-navy dark:text-white">File Requests</h1>
        <div className="flex items-center gap-1 border border-slate-200 dark:border-[#2A2A4A] bg-white dark:bg-[#1A1A2E]">
          <Link
            to="/requests"
            className="p-2 text-slate-500 dark:text-[#8888AA] hover:text-bb-navy dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/10 transition-colors"
            title="List view"
          >
            <List size={16} />
          </Link>
          <span className="p-2 text-bb-navy dark:text-white bg-slate-100 dark:bg-white/10">
            <Columns size={16} />
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="text-sm text-red-600">{apiError(error)}</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto overflow-y-hidden pb-4 h-[calc(100vh-180px)]">
          {grouped.map((col) => (
            <div
              key={col.label}
              className={cn(
                'flex flex-col flex-1 min-w-[280px] bg-slate-50 dark:bg-[#13132A] border-t-2 border border-slate-200 dark:border-[#2A2A4A]',
                col.color,
              )}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-[#2A2A4A]">
                <span className="text-xs font-semibold text-slate-700 dark:text-white uppercase tracking-wide">
                  {col.label}
                </span>
                <span className="text-xs text-slate-500 dark:text-[#8888AA] font-medium">
                  {col.requests.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {col.requests.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-[#8888AA] text-center py-4">No items</p>
                ) : (
                  col.requests.map((req) => <RequestCard key={req.id} req={req} isClient={isClient} />)
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
